import type { User } from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { FIRESTORE_COLLECTIONS, firestore } from '../firebase';

const getFirebaseErrorDetails = (error: unknown) => ({
  code: typeof error === 'object' && error !== null && 'code' in error
    ? String((error as { code?: unknown }).code || '')
    : '',
  message: error instanceof Error ? error.message : String(error),
});

export async function ensureFirestoreUserDocument(user: User) {
  const path = `${FIRESTORE_COLLECTIONS.users}/${user.uid}`;
  const userRef = doc(firestore, FIRESTORE_COLLECTIONS.users, user.uid);

  console.log('[Tonyte][AuthBootstrap] checking Firestore user document', {
    uid: user.uid,
    email: user.email || '',
    providerIds: user.providerData.map(provider => provider.providerId),
    path,
  });

  try {
    const snapshot = await getDoc(userRef);
    console.log('[Tonyte][AuthBootstrap] Firestore user lookup complete', {
      uid: user.uid,
      path,
      exists: snapshot.exists(),
      role: snapshot.data()?.role || null,
    });

    if (!snapshot.exists()) {
      console.log('[Tonyte][AuthBootstrap] creating Firestore user document', {
        uid: user.uid,
        path,
        role: 'member',
      });
      await setDoc(userRef, {
        id: user.uid,
        email: user.email || '',
        name: user.displayName || user.email?.split('@')[0] || 'Tonyte User',
        photo: user.photoURL || '',
        profilePhotoURL: user.photoURL || '',
        role: 'member',
        isAdmin: false,
        isHost: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastActiveAt: serverTimestamp(),
      });
      console.log('[Tonyte][AuthBootstrap] Firestore user document created', {
        uid: user.uid,
        path,
        role: 'member',
      });
      return;
    }

    if (!snapshot.data()?.role) {
      console.log('[Tonyte][AuthBootstrap] assigning missing default role', {
        uid: user.uid,
        path,
        role: 'member',
      });
      await setDoc(userRef, {
        role: 'member',
        updatedAt: serverTimestamp(),
      }, { merge: true });
      console.log('[Tonyte][AuthBootstrap] missing default role assigned', {
        uid: user.uid,
        path,
        role: 'member',
      });
      return;
    }

    console.log('[Tonyte][AuthBootstrap] existing Firestore user is ready', {
      uid: user.uid,
      path,
      role: snapshot.data()?.role,
    });
  } catch (error) {
    console.error('[Tonyte][AuthBootstrap] Firestore user bootstrap failed', {
      uid: user.uid,
      path,
      operation: 'getDoc/setDoc',
      ...getFirebaseErrorDetails(error),
      error,
    });
    throw error;
  }
}
